import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useToast } from '@powercore/ui';
import { ArrowLeft, Check, Download, Copy, Terminal, Lock, Sparkles, FileText, Wand2, Loader2 } from 'lucide-react';
import { api, type SkillDetail, type CompanyProfile, type SkillOutput } from '../lib/api';
import { SkillIcon } from '../components/SkillIcon';
import { relativeDate } from '../lib/date';

const TOOL_LABELS: Record<string, string> = {
  claude_code: 'Claude Code',
  chatgpt: 'ChatGPT',
  codex: 'Codex / Cursor',
  cursor: 'Cursor',
  gemini: 'Gemini',
};
const font = { fontFamily: "'Outfit', sans-serif" };

function price(cents?: number | null) {
  if (!cents) return null;
  return `$${(cents / 100).toFixed(0)}`;
}

export function SkillDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeTool, setActiveTool] = useState<string>('');

  // In-app run against the company profile.
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [runInput, setRunInput] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [outputs, setOutputs] = useState<SkillOutput[]>([]);

  const loadOutputs = () => {
    api.listSkillOutputs(slug).then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (Array.isArray(data)) setOutputs(data);
    });
  };

  const load = () => {
    setLoading(true);
    api.getSkill(slug).then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (data && data.slug) {
        setSkill(data);
        setActiveTool((prev) => prev || data.guides?.[0]?.tool || '');
        if (data.owned) {
          api.getProfile().then((r) => setProfile((r.data as any)?.data || r.data || null));
          loadOutputs();
        }
      }
      setLoading(false);
    });
  };
  useEffect(load, [slug]);

  const run = async () => {
    setRunning(true);
    setRunResult(null);
    const res = await api.runSkill(slug, runInput.trim() || undefined);
    setRunning(false);
    if (res.error) {
      addToast({ title: 'Run failed', description: res.error.message, variant: 'error' });
      return;
    }
    const data = (res.data as any)?.data || res.data;
    if (data?.output) {
      setRunResult(data.output);
      loadOutputs();
    }
  };

  const copyOutput = async (text: string) => {
    await navigator.clipboard.writeText(text);
    addToast({ title: 'Copied', variant: 'success' });
  };

  const acquire = async (mode: 'subscription' | 'one_time') => {
    setBusy(true);
    const res = await api.acquireSkill(slug, mode);
    setBusy(false);
    if (res.error) {
      addToast({ title: 'Could not get skill', description: res.error.message, variant: 'error' });
      return;
    }
    addToast({ title: 'Access granted', description: mode === 'subscription' ? `You now have every skill in ${skill?.category?.name}.` : 'Skill added to your library.', variant: 'success' });
    load();
  };

  const release = async () => {
    setBusy(true);
    await api.releaseSkill(slug);
    setBusy(false);
    load();
  };

  const download = async () => {
    try {
      await api.downloadSkill(slug);
      addToast({ title: 'Downloading', description: `${slug}.zip is on its way.`, variant: 'success' });
    } catch {
      addToast({ title: 'Download failed', description: 'Please try again.', variant: 'error' });
    }
  };

  const copySkill = async () => {
    const res = await api.getSkillPackage(slug);
    const data = (res.data as any)?.data || res.data;
    const primary = data?.files?.find((f: any) => f.is_primary) || data?.files?.[0];
    if (!primary) {
      addToast({ title: 'Nothing to copy', description: 'This skill has no content yet.', variant: 'error' });
      return;
    }
    await navigator.clipboard.writeText(primary.content);
    addToast({ title: 'Copied', description: 'SKILL.md copied — paste it into ChatGPT or your tool.', variant: 'success' });
  };

  const copyCommand = async () => {
    if (!skill) return;
    await navigator.clipboard.writeText(skill.install_command);
    addToast({ title: 'Command copied', variant: 'success' });
  };

  if (loading) return <div className="py-24 text-center text-gray-400" style={font}>Loading…</div>;
  if (!skill) return <div className="py-24 text-center text-gray-400" style={font}>Skill not found.</div>;

  const activeGuide = skill.guides?.find((g) => g.tool === activeTool);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800" style={font}>
        <ArrowLeft className="h-4 w-4" /> Back to store
      </button>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
        {/* Main */}
        <div>
          <div className="flex items-start gap-4">
            <SkillIcon icon={skill.icon} emoji={skill.emoji} accent={skill.category?.accent} size={64} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {skill.category && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{skill.category.name}</span>}
                <span className="text-xs text-gray-400">v{skill.version} · by {skill.author}{skill.updated_at ? ` · Updated ${relativeDate(skill.updated_at)}` : ''}</span>
              </div>
              <h1 className="mt-1.5 text-2xl font-semibold text-gray-900" style={font}>{skill.name}</h1>
              <p className="mt-1 text-gray-600" style={font}>{skill.tagline}</p>
            </div>
          </div>

          {/* Compatible tools */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Works in</span>
            {(skill.compatible_tools || []).map((t) => (
              <span key={t} className="rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600">{TOOL_LABELS[t] || t}</span>
            ))}
          </div>

          {/* Run for your company — the in-app, profile-tailored action (owned) */}
          {skill.owned && (
            <div className="mt-6 rounded-2xl border border-mazo-200 bg-gradient-to-br from-mazo-50/70 to-white p-5">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900" style={font}>
                <Wand2 className="h-4 w-4 text-mazo-600" /> Run for your company
              </div>
              {profile && !profile.one_liner ? (
                <p className="mt-1 text-xs text-gray-500" style={font}>
                  <Link to="/company" className="font-medium text-mazo-700 underline underline-offset-2">Set up your company</Link> first and this runs tailored to you — otherwise you’ll get a generic result.
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500" style={font}>
                  Generates a deliverable tailored to {profile?.company_name || 'your company'}{profile?.version ? ` (profile v${profile.version})` : ''}.
                </p>
              )}
              <label className="mt-3 block text-xs font-medium text-gray-600" style={font}>
                Raw material &amp; instructions — what should this be based on?
              </label>
              <textarea
                value={runInput}
                onChange={(e) => setRunInput(e.target.value)}
                placeholder="Paste your raw material: a take or opinion, a customer story, meeting notes or a transcript, or a rough draft. The more specific, the sharper the result. (Some skills also work from your company profile alone.)"
                rows={4}
                className="mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
                style={font}
              />
              <button
                onClick={run}
                disabled={running}
                className="mt-2 flex items-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700 disabled:opacity-60"
                style={font}
              >
                {running ? <><Loader2 className="h-4 w-4 animate-spin" /> Running…</> : <><Wand2 className="h-4 w-4" /> Run skill</>}
              </button>

              {runResult && (
                <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                    <span className="text-xs font-medium text-gray-500" style={font}>Your result</span>
                    <button onClick={() => copyOutput(runResult)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900" style={font}>
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  </div>
                  <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap p-4 text-[13px] leading-relaxed text-gray-800" style={font}>{runResult}</pre>
                </div>
              )}

              {outputs.length > 0 && (
                <div className="mt-3">
                  <div className="mb-1.5 text-xs font-medium text-gray-400" style={font}>Saved runs</div>
                  <div className="space-y-1">
                    {outputs.slice(0, 6).map((o) => (
                      <button
                        key={o.id}
                        onClick={() => setRunResult(o.output)}
                        className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-xs text-gray-600 hover:bg-white"
                        style={font}
                      >
                        <span className="truncate">{o.input || o.title || 'Run'}</span>
                        <span className="ml-3 flex-shrink-0 text-gray-400">{relativeDate(o.created_at)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {skill.description && (
            <p className="mt-6 whitespace-pre-wrap text-[15px] leading-relaxed text-gray-700" style={font}>{skill.description}</p>
          )}

          {/* Value props */}
          {skill.value_props && skill.value_props.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-semibold text-gray-900" style={font}>What it does</h3>
              <ul className="space-y-2">
                {skill.value_props.map((v, i) => (
                  <li key={i} className="flex items-start gap-2 text-[15px] text-gray-700" style={font}>
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" /> <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Preview / contents */}
          <div className="mt-8">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900" style={font}>
              <FileText className="h-4 w-4 text-gray-400" /> {skill.owned ? 'Skill contents' : 'Preview'}
            </h3>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <pre className="max-h-72 overflow-auto bg-gray-50 p-4 text-[13px] leading-relaxed text-gray-700 whitespace-pre-wrap">{skill.owned ? (skill.files.find((f) => f.is_primary)?.content || skill.preview) : skill.preview}{!skill.owned && '\n\n…'}</pre>
              {!skill.owned && (
                <div className="flex items-center gap-2 border-t border-gray-200 bg-white px-4 py-2.5 text-xs text-gray-500" style={font}>
                  <Lock className="h-3.5 w-3.5" /> Get this skill to unlock the full package{skill.files.length > 1 ? ` (${skill.files.length} files)` : ''}.
                </div>
              )}
            </div>
          </div>

          {/* How to use — per-tool guides (visible once owned) */}
          {skill.owned && skill.guides && skill.guides.length > 0 && (
            <div className="mt-8">
              <h3 className="mb-3 text-sm font-semibold text-gray-900" style={font}>How to use it</h3>
              <div className="mb-3 flex flex-wrap gap-2">
                {skill.guides.map((g) => (
                  <button
                    key={g.tool}
                    onClick={() => setActiveTool(g.tool)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${activeTool === g.tool ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
                    style={font}
                  >
                    {g.title || TOOL_LABELS[g.tool] || g.tool}
                  </button>
                ))}
              </div>
              {activeGuide && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <pre className="whitespace-pre-wrap text-[14px] leading-relaxed text-gray-700" style={font}>{activeGuide.steps}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar — acquire / install */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            {skill.owned ? (
              <>
                <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">
                  <Check className="h-4 w-4" /> In your library
                </div>
                <div className="space-y-2">
                  <button onClick={download} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-black" style={font}>
                    <Download className="h-4 w-4" /> Download package (.zip)
                  </button>
                  <button onClick={copySkill} className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50" style={font}>
                    <Copy className="h-4 w-4" /> Copy SKILL.md
                  </button>
                </div>

                <div className="mt-4 rounded-xl bg-gray-50 p-3">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-gray-500" style={font}>
                    <Terminal className="h-3.5 w-3.5" /> Install command
                    <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">Coming soon</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 overflow-x-auto rounded-md bg-gray-900 px-2.5 py-1.5 text-[12px] text-gray-100">{skill.install_command}</code>
                    <button onClick={copyCommand} className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:text-gray-900" title="Copy command"><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-gray-400" style={font}>One-line CLI install is on the way — for now use Download or Copy above.</p>
                </div>

                <button onClick={release} disabled={busy} className="mt-4 w-full text-center text-xs text-gray-400 hover:text-gray-600" style={font}>
                  Remove from library (testing)
                </button>
              </>
            ) : (
              <>
                <div className="mb-1 text-sm font-medium text-gray-500" style={font}>Get this skill</div>
                <div className="mb-4 text-2xl font-semibold text-gray-900" style={font}>
                  {price(skill.price_one_time_cents) ? <>{price(skill.price_one_time_cents)}<span className="text-sm font-normal text-gray-400"> one-time</span></> : <span className="text-base font-normal text-gray-500">Included with subscription</span>}
                </div>

                {skill.category && (
                  <button onClick={() => acquire('subscription')} disabled={busy} className="mb-2 flex w-full items-center justify-center gap-2 rounded-xl bg-mazo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-mazo-700 disabled:opacity-60" style={font}>
                    <Sparkles className="h-4 w-4" /> Subscribe to {skill.category.name}
                  </button>
                )}
                {(skill.pricing_model === 'both' || skill.pricing_model === 'one_time') && skill.price_one_time_cents ? (
                  <button onClick={() => acquire('one_time')} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60" style={font}>
                    Buy once · {price(skill.price_one_time_cents)}
                  </button>
                ) : null}

                <p className="mt-3 text-xs leading-relaxed text-gray-400" style={font}>
                  Subscribe and every skill in {skill.category?.name || 'this category'} unlocks — with updates as they ship.
                </p>
              </>
            )}
          </div>

          {skill.what_you_get && (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-5">
              <h4 className="mb-2 text-sm font-semibold text-gray-900" style={font}>In the package</h4>
              <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-600" style={font}>{skill.what_you_get}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SkillDetailPage;
